import React, { FC, ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowRight, ChatDotsFill, GeoAltFill } from 'react-bootstrap-icons';
import { useSite } from '../../customFrontend/SiteContext';
import { Disclosure } from '../../customFrontend/parts';
import { SiteManifest } from '../../customFrontend/types';
import { ChatappCity, ChatappLabels, ChatappTopic } from './types';

/** The call to action: opens the signup modal. */
export const Cta: FC<{ children: ReactNode; light?: boolean }> = ({ children, light }) => {
  const { openSignup } = useSite();
  return (
    <button type="button" className={`btn cx-cta${light ? ' cx-cta--light' : ''}`} onClick={openSignup}>
      <ChatDotsFill aria-hidden="true" className="cx-cta-icon" />
      {children}
    </button>
  );
};

/** The brand's app icon: a chat bubble on the brand gradient. */
export const AppIcon: FC = () => (
  <span className="cx-app-icon" aria-hidden="true"><ChatDotsFill /></span>
);

/**
 * App-style notification chips with the product's facts (free signup, free
 * first messages). They come from the app, never from a person: the photos
 * beside them are mood imagery only.
 */
export const Notifications: FC<{ site: SiteManifest; items: { title: string; text: string }[]; now: string }> = ({ site, items, now }) => (
  <ul className="cx-notes">
    {items.map((item) => (
      <li key={item.title} className="cx-note">
        <AppIcon />
        <span className="cx-note-body">
          <span className="cx-note-head"><strong>{site.name}</strong><span className="cx-note-time">{now}</span></span>
          <span className="cx-note-title">{item.title}</span>
          <span className="cx-note-text">{item.text}</span>
        </span>
      </li>
    ))}
  </ul>
);

/** Tiles linking to every city: two columns on phones, five from 768px. */
export const CityTiles: FC<{ cities: ChatappCity[]; labels: ChatappLabels }> = ({ cities, labels }) => (
  <nav aria-label={labels.citiesNav}>
    <ul className="cx-tiles">
      {cities.map((city) => (
        <li key={city.path} className={`cx-tile cx-city--${city.slug}`}>
          <GeoAltFill aria-hidden="true" className="cx-tile-pin" />
          <Link to={city.path} className="cx-tile-link">
            {/* "Sexchat Amsterdam": the kind on a small line of its own, the city large */}
            <span className="cx-tile-kind">{labels.cityLinkKind}</span>{' '}
            <span className="cx-tile-name">{city.name}</span>
          </Link>
          <span className="cx-tile-province">{city.province}</span>
        </li>
      ))}
    </ul>
  </nav>
);

/** Home's card for each topic page, below the city tiles: wide, not another city tile. */
export const TopicCards: FC<{ topics: ChatappTopic[] }> = ({ topics }) => (
  <ul className="cx-topics">
    {topics.map((topic) => (
      <li key={topic.path} className={`cx-topic cx-topic--${topic.slug}`}>
        <span className="cx-dot" aria-hidden="true" />
        <span className="cx-topic-body">
          <span className="cx-topic-title">{topic.link.title}</span>
          <span className="cx-topic-text">{topic.link.text}</span>
        </span>
        <Link to={topic.path} className="cx-topic-link">
          {topic.link.anchor} <ArrowRight aria-hidden="true" />
        </Link>
      </li>
    ))}
  </ul>
);

/** Pills linking to the other cities (a city page): they wrap at every width. */
export const CityPills: FC<{ cities: ChatappCity[]; exclude: string; labels: ChatappLabels }> = ({ cities, exclude, labels }) => (
  <nav aria-label={labels.citiesNav}>
    <ul className="cx-pills">
      {cities.filter((city) => city.path !== exclude).map((city) => (
        <li key={city.path} className={`cx-pill cx-city--${city.slug}`}>
          <Link to={city.path}>{labels.cityLinkKind} {city.name}</Link>
        </li>
      ))}
    </ul>
  </nav>
);

/** The brand's section above the standard footer: every page and the disclosure. */
export const SiteFooter: FC<{ site: SiteManifest; cities: ChatappCity[]; labels: ChatappLabels; topics?: ChatappTopic[] }> = ({ site, cities, labels, topics = [] }) => (
  <section className="cx-site-footer" aria-label={labels.footerNav}>
    <div className="container">
      <ul className="cx-footer-links">
        <li><Link to="/">{labels.footerHome}</Link></li>
        {cities.map((city) => (
          <li key={city.path}><Link to={city.path}>{labels.cityLinkKind} {city.name}</Link></li>
        ))}
        {topics.map((topic) => (
          <li key={topic.path}><Link to={topic.path}>{topic.link.anchor}</Link></li>
        ))}
      </ul>
      <Disclosure name={site.name} language={site.language} />
    </div>
  </section>
);

/** The closing band: brand gradient, one call to action. */
export const FinalCta: FC<{ title: string; text: string; button: string; className?: string }> = ({ title, text, button, className }) => (
  <section className={`cx-final ${className ?? ''}`}>
    <div className="container">
      <h2>{title}</h2>
      <p>{text}</p>
      <Cta light>{button}</Cta>
    </div>
  </section>
);

